import { API } from '@canvas-logic/clp-viewer3d';

const AR_VIEWER_URL = window.APP_CONFIG.AR_VIEWER_URL;

const GLB_CONTENT_TYPE = 'model/gltf-binary';

/**
 * A short-lived permission slip to write one object to the AR bucket, minted by `clp-storage`.
 * `fields` are signed and must be submitted unchanged; `maxBytes` is the same number GCS enforces
 * through the policy, so the pre-check here and the server's limit cannot drift (ADR-0090).
 */
type UploadTicket = {
  url: string;
  fields: Record<string, string>;
  maxBytes: number;
  softLimitBytes: number;
  id: string;
};

type SizeOutcome = 'rejected' | 'soft-threshold-exceeded';

export class ArModelTooLargeError extends Error {
  constructor(readonly size: number, readonly limit: number) {
    super(`AR model is ${size} bytes, over the limit of ${limit} bytes`);
    this.name = 'ArModelTooLargeError';
  }
}

export class WebArService {
  constructor(private readonly api: API) {}

  async generateArLink(projectId: string): Promise<string> {
    const [gltfId] = await Promise.all([
      this.generateArLinkGLTF(projectId),
      // this.generateArLinkUSDZ()
    ]);
    // const url = `${AR_VIEWER_URL}?gltfId=${gltfId}&project=${AR_PROJECT}`;
    const url = `${AR_VIEWER_URL}/link/${gltfId}?project=${projectId}`;
    return url;
  }

  private async generateArLinkGLTF(projectId: string): Promise<string> {
    const glb = await this.api.exportGltfScene();
    const ticket = await this.requestUploadTicket(projectId);
    const size = glb.byteLength;

    // Slots make scene composition dynamic, so this is the first moment the size exists at all.
    if (size > ticket.maxBytes) {
      void this.reportSize(projectId, size, ticket.maxBytes, 'rejected');
      throw new ArModelTooLargeError(size, ticket.maxBytes);
    }
    if (size > ticket.softLimitBytes) {
      void this.reportSize(projectId, size, ticket.softLimitBytes, 'soft-threshold-exceeded');
    }

    await this.upload(ticket, glb);
    // Awaited, not fire-and-forget: on mobile `useGetArLink` calls `window.location.replace` as soon as
    // this resolves, and the unload cancels any request still in flight.
    await this.markUploaded(projectId, ticket.id);
    return ticket.id;
  }

  private async requestUploadTicket(projectId: string): Promise<UploadTicket> {
    const response = await fetch(`${AR_VIEWER_URL}/upload-ticket?project=${projectId}`, { method: 'POST' });
    if (!response.ok) {
      throw new Error(`AR upload ticket request failed with status ${response.status}`);
    }
    return response.json();
  }

  /**
   * Posts straight to GCS — the model bytes never pass through App Engine, which is what removes the
   * 32MB request limit that used to reject large exports before any code of ours ran.
   */
  private async upload(ticket: UploadTicket, glb: ArrayBuffer): Promise<void> {
    const form = new FormData();
    // GCS requires every signed field, unchanged, and the file part last.
    Object.entries(ticket.fields).forEach(([name, value]) => form.append(name, value));
    form.append('file', new Blob([glb], { type: GLB_CONTENT_TYPE }));

    const response = await fetch(ticket.url, { method: 'POST', body: form });
    if (!response.ok) {
      throw new Error(`AR model upload failed with status ${response.status}`);
    }
  }

  /**
   * The signed policy cannot carry `x-goog-custom-time`, so a model that is never viewed would carry no
   * `customTime` and the bucket's 30-day lifecycle rule would never match it (ADR-0090). A failure here
   * costs retention on one object, not the AR link the user is waiting for, so it is logged, not thrown.
   */
  private async markUploaded(projectId: string, id: string): Promise<void> {
    try {
      const response = await fetch(`${AR_VIEWER_URL}/uploaded/${id}?project=${projectId}`, { method: 'POST' });
      if (!response.ok) {
        throw new Error(`AR retention stamp failed with status ${response.status}`);
      }
    } catch (e) {
      // Never fatal: the model is uploaded and the link works — only its expiry clock is missing.
      console.error(e);
    }
  }

  /**
   * A size failure that reaches only the browser console is operationally invisible — which is what
   * made the original bug expensive to diagnose. Reporting is best-effort and never blocks the user.
   */
  private async reportSize(projectId: string, size: number, limit: number, outcome: SizeOutcome): Promise<void> {
    try {
      await fetch(`${AR_VIEWER_URL}/ar-size-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project: projectId, size, limit, outcome }),
      });
    } catch (e) {
      console.error(e);
    }
  }
}
